import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Heart, ShieldCheck, Star } from "lucide-react";
import { IMAGES } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const roles = [
  {
    icon: Star,
    title: "Director / Nominated Supervisor",
    desc: "Leads the service day to day - overseeing programs, compliance, family partnerships and the wellbeing of every child and educator.",
  },
  {
    icon: GraduationCap,
    title: "Educational Leader",
    desc: "Guides the educational program across BSC, ASC and Vacation Care, mentoring educators and championing the My Time, Our Place framework.",
  },
  {
    icon: Heart,
    title: "Educators",
    desc: "Our consistent team of qualified and studying educators who greet children by name, plan from their interests and make the service feel like home.",
  },
  {
    icon: ShieldCheck,
    title: "Inclusion & Support",
    desc: "Dedicated support so children with additional needs can participate fully, in partnership with families and allied professionals.",
  },
];

const commitments = [
  "Consistent, familiar faces - low staff turnover matters to children",
  "Educators qualified or actively studying towards ACECQA-approved qualifications",
  "All educators hold current Working with Vulnerable People registration and first aid training",
  "Ongoing professional development and reflective practice",
];

const Team = () => (
  <div data-testid="team-page">
    <Seo
      title="Our Team | Telopea Park OSHC"
      description="Meet the warm, qualified educators and leadership team caring for children at Telopea Park OSHC in Barton, Canberra."
      path="/team"
    />
    <PageHeader
      testId="team-header"
      eyebrow="Our Team"
      title="The people who make it feel like home"
      description="Children thrive when they are cared for by familiar, trusted adults. Our stable, qualified team is at the heart of everything families value about Telopea Park OSHC."
    />

    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24" data-testid="team-roles">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {roles.map((r, i) => (
          <FadeIn key={r.title} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid={`team-role-${i + 1}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <r.icon className="h-6 w-6" />
              </div>
              <h2 className="mt-6 font-display text-2xl font-semibold text-navy">{r.title}</h2>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{r.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>

    <section className="bg-surface border-y border-navy-100" data-testid="team-commitments">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <FadeIn>
            <img
              src={IMAGES.friends}
              alt="Children in Telopea Park OSHC hi-vis vests exploring an exhibit on an excursion"
              className="w-full rounded-3xl object-cover shadow-lg aspect-[4/3]"
              data-testid="team-image"
            />
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-waratah">Our Commitment</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">
              What families can expect from our team
            </h2>
            <ul className="mt-8 space-y-4">
              {commitments.map((c, i) => (
                <li key={c} className="flex items-start gap-3" data-testid={`team-commitment-${i + 1}`}>
                  <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-waratah" />
                  <span className="text-lg text-slate-600 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="team-contact-link"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-waratah px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600"
            >
              Come and meet us <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </div>
    </section>
  </div>
);

export default Team;
